import { TreeNode as TreeNodeType } from "@/utils/treeUtils";
import React from "react";
import { View } from "react-native";
import { TreeNode } from "./TreeNode";
import { TreeNodeInlineInput } from "./TreeNodeInlineInput";

/** Ermittelt die ID des letzten Knotens in depth-first Reihenfolge (bei expandedIds). */
function getLastNodeId<T>(
  nodeList: TreeNodeType<T>[],
  expandedIds: Set<string>
): string | null {
  if (nodeList.length === 0) return null;
  const lastNode = nodeList[nodeList.length - 1];
  const expanded = expandedIds.has(lastNode.id);
  const hasChildren = lastNode.children.length > 0;
  if (expanded && hasChildren) {
    const inChild = getLastNodeId(lastNode.children, expandedIds);
    return inChild ?? lastNode.id;
  }
  return lastNode.id;
}

type TreeViewProps<T> = {
  /** Tree nodes to render */
  nodes: TreeNodeType<T>[];
  /** Set of expanded node IDs */
  expandedIds: Set<string>;
  /** Toggle expand state of a node */
  onToggleExpand: (id: string) => void;
  /** Called when a node is pressed */
  onNodePress: (node: TreeNodeType<T>) => void;
  /** Called when add child is pressed on a node */
  onAddChild?: (parentId: string) => void;
  /** Render function for node content */
  renderNodeContent: (node: TreeNodeType<T>) => React.ReactNode;
  /** Optional trailing action per node (e.g. delete button) */
  renderNodeTrailing?: (node: TreeNodeType<T>) => React.ReactNode;
  /** Parent ID where inline creation is active */
  inlineCreatingParentId?: string | null;
  /** Placeholder for inline input */
  inlineInputPlaceholder?: string;
  /** Called when inline creation is confirmed */
  onInlineCreateConfirm?: (parentId: string | null, value: string) => void;
  /** Called when inline creation is cancelled */
  onInlineCreateCancel?: () => void;
};

export function TreeView<T>({
  nodes,
  expandedIds,
  onToggleExpand,
  onNodePress,
  onAddChild,
  renderNodeContent,
  renderNodeTrailing,
  inlineCreatingParentId,
  inlineInputPlaceholder = "Name eingeben...",
  onInlineCreateConfirm,
  onInlineCreateCancel,
}: TreeViewProps<T>) {
  const lastNodeId = React.useMemo(
    () => getLastNodeId(nodes, expandedIds),
    [nodes, expandedIds]
  );

  const renderNodes = (
    nodeList: TreeNodeType<T>[],
    parentId: string | null = null
  ): React.ReactNode => {
    return (
      <>
        {nodeList.map((node) => {
          const expanded = expandedIds.has(node.id);
          const hasChildren = node.children.length > 0;

          return (
            <React.Fragment key={node.id}>
              <TreeNode
                depth={node.depth}
                expanded={expanded}
                hasChildren={hasChildren}
                isLastInTree={node.id === lastNodeId}
                onToggleExpand={() => onToggleExpand(node.id)}
                onPress={() => onNodePress(node)}
                onAddChild={onAddChild ? () => onAddChild(node.id) : undefined}
                trailing={renderNodeTrailing?.(node)}
              >
                {renderNodeContent(node)}
              </TreeNode>

              {/* Render inline input as first child if creating under this node */}
              {inlineCreatingParentId === node.id &&
                onInlineCreateConfirm &&
                onInlineCreateCancel && (
                  <TreeNodeInlineInput
                    depth={node.depth + 1}
                    placeholder={inlineInputPlaceholder}
                    onConfirm={(value) =>
                      onInlineCreateConfirm(node.id, value)
                    }
                    onCancel={onInlineCreateCancel}
                  />
                )}

              {/* Render children if expanded */}
              {expanded && hasChildren && renderNodes(node.children, node.id)}
            </React.Fragment>
          );
        })}

        {/* Render inline input at root level if creating with null parent */}
        {parentId === null &&
          inlineCreatingParentId === "__root__" &&
          onInlineCreateConfirm &&
          onInlineCreateCancel && (
            <TreeNodeInlineInput
              depth={0}
              placeholder={inlineInputPlaceholder}
              onConfirm={(value) => onInlineCreateConfirm(null, value)}
              onCancel={onInlineCreateCancel}
            />
          )}
      </>
    );
  };

  return <View>{renderNodes(nodes)}</View>;
}
